import React, { useContext, useState } from "react";
import ReactGA from "react-ga4";

import { uid } from "uid";
import { PrayerModalContext } from "../../contexts/prayer-modal-context";
import {
  createMailInFirestoreDb,
  createPrayerInFirestoreDb,
} from "../../utils/firebase-utils";

import { faRectangleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "../form-components/button";
import CheckboxInput from "../form-components/checkbox-input";
import "./submit-prayer.styles.css";
import FormInput from "../form-components/form-input";
import { BurgerMenuContext } from "../../contexts/burger-menu-context";
import { GuestSignUpModalContext } from "../../contexts/guest-signup-modal-context";
import { SaveGuestDetailsContext } from "../../contexts/save-guest-details-context";

const defaultFormFields = {
  message: "",
  anonymous: false,
  displayNameCheckbox: false,
  displayName: "",
  emailCheckbox: false,
  email: "",
  saveDetailsCheckbox: false,
  supportName: "",
};

const SubmitPrayerForm = () => {
  const [formFields, setFormFields] = useState(defaultFormFields);
  const [anonCheckboxState, setAnonCheckboxState] = useState(false);
  const [displayNameCheckboxState, setDisplayNameCheckboxState] =
    useState(false);
  const [emailCheckboxState, setEmailCheckboxState] = useState(false);
  const [saveDetailsCheckboxState, setSaveDetailsCheckboxState] =
    useState(false);
  const [supportCheckboxState, setSupportCheckboxState] = useState(false);
  const [notifications, setNotifications] = useState(false);

  const { message, displayName, email } = formFields;

  const { setOpenSubmitPrayerModal } = useContext(PrayerModalContext);
  const { setOpenGuestSignUpModal } = useContext(GuestSignUpModalContext);
  const { setGuestDetails } = useContext(SaveGuestDetailsContext);
  const { isMenuOpen, toggleMenu } = useContext(BurgerMenuContext);

  if (isMenuOpen) toggleMenu();

  const storageUser = localStorage.getItem("user");
  const currentUser = JSON.parse(storageUser);

  const isGuestUser = currentUser.isAnonymous === true;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormFields({ ...formFields, [name]: value });
  };

  const handleCheckboxChange = () => {
    setAnonCheckboxState(!anonCheckboxState);
  };

  const handleDisplayNameCheckboxChange = () => {
    setDisplayNameCheckboxState(!displayNameCheckboxState);
  };

  const handleEmailCheckboxChange = () => {
    setEmailCheckboxState(!emailCheckboxState);
  };

  const handleSaveDetailsCheckboxChange = () => {
    setSaveDetailsCheckboxState(!saveDetailsCheckboxState);
  };

  const handleSupportCheckboxChange = () => {
    setSupportCheckboxState(!supportCheckboxState);
  };

  const handleNotificationsChange = () => {
    setNotifications(!notifications);
  };

  const messageLength = message.length;
  const charactersRemaining = 200 - messageLength;

  const handleModelClose = () => {
    setOpenSubmitPrayerModal(false);
  };

  const resetFormFields = () => {
    setFormFields(defaultFormFields);
    setAnonCheckboxState(false);
  };

  const getDisplayName = () => {
    if (anonCheckboxState) {
      return "Anonymous";
    } else if (isGuestUser && !displayNameCheckboxState) {
      return "Anonymous";
    } else if (isGuestUser && displayNameCheckboxState) {
      return displayName;
    } else {
      return currentUser.displayName;
    }
  };

  const getEmail = () => {
    if (email !== "") {
      return email;
    } else if (currentUser.email) {
      return currentUser.email;
    } else {
      return false;
    }
  };

  const shouldSendEmailNotifications = () => {
    if ((emailCheckboxState && email !== "") || notifications) {
      return true;
    } else {
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    ReactGA.event({
      category: "form-actions",
      action: "submit-prayer",
    });

    const prayerId = uid();
    const lastPrayedTimestamp = 0;
    const prayerDisplayName = getDisplayName();
    const email = getEmail();
    const emailNotifications = shouldSendEmailNotifications();
    const userName = currentUser.displayName;

    try {
      await createPrayerInFirestoreDb(
        prayerId,
        message,
        prayerDisplayName,
        currentUser.uid,
        email,
        lastPrayedTimestamp,
        emailNotifications
      );

      if (supportCheckboxState) {
        createMailInFirestoreDb(email, userName, message, true);
      }

      if (saveDetailsCheckboxState) {
        setGuestDetails({ displayName, email });
        setOpenGuestSignUpModal(true);
      }

      resetFormFields();
      setOpenSubmitPrayerModal(false);
    } catch (error) {
      console.log("error writing prayer to db", error);
    }
  };

  return (
    <>
      <div className="prayer-modal">
        <div className="prayer-modal-content">
          <div className="prayer-modal-header">
            <Button
              className="prayer-modal-close-button"
              onClick={handleModelClose}
            >
              <FontAwesomeIcon
                icon={faRectangleXmark}
                style={{ color: "#363832" }}
                size="2xl"
              />
            </Button>
          </div>

          <h1 className="prayer-modal-heading">New Prayer</h1>

          <form onSubmit={handleSubmit}>
            <div>
              <textarea
                id="submit-prayer-text"
                rows="6"
                cols="28"
                maxLength="200"
                required
                onChange={handleChange}
                name="message"
                placeholder="your prayer..."
                value={message}
              ></textarea>
              <div className="character-count">
                Characters remaining: {charactersRemaining}
              </div>
            </div>

            {!isGuestUser && (
              <>
                <div className="anon-checkbox">
                  <CheckboxInput
                    label="Post prayer anonymously"
                    inputType="checkbox"
                    type="checkbox"
                    onChange={handleCheckboxChange}
                    name="anonymous"
                    checked={anonCheckboxState}
                  />
                </div>
                <div className="notifications-checkbox">
                  <CheckboxInput
                    label="Email me when someone prays"
                    inputType="checkbox"
                    type="checkbox"
                    onChange={handleNotificationsChange}
                    name="anonymous"
                    checked={notifications}
                  />
                </div>
                <div className="email-checkbox">
                  <CheckboxInput
                    label="I'd like to speak to someone"
                    inputType="checkbox"
                    type="checkbox"
                    onChange={handleSupportCheckboxChange}
                    name="anonymous"
                    checked={supportCheckboxState}
                  />
                </div>
              </>
            )}

            {isGuestUser && (
              <div>
                <div className="anon-checkbox">
                  <CheckboxInput
                    label="Enter display name"
                    inputType="checkbox"
                    type="checkbox"
                    onChange={handleDisplayNameCheckboxChange}
                    name="displayNameCheckbox"
                    checked={displayNameCheckboxState}
                  />
                </div>
                {displayNameCheckboxState && (
                  <FormInput
                    inputType="guestDisplayName"
                    type="text"
                    required
                    onChange={handleChange}
                    name="displayName"
                    placeholder="your name"
                    value={displayName}
                  />
                )}
                <div className="email-checkbox">
                  <CheckboxInput
                    label="Email me when someone prays"
                    inputType="checkbox"
                    type="checkbox"
                    onChange={handleEmailCheckboxChange}
                    name="emailCheckbox"
                    checked={emailCheckboxState}
                  />
                </div>
                {emailCheckboxState && (
                  <div>
                    <FormInput
                      inputType="guestEmail"
                      type="email"
                      required
                      onChange={handleChange}
                      name="email"
                      placeholder="enter email"
                      value={email}
                    />
                    <div className="save-details-checkbox">
                      <CheckboxInput
                        label="Save details for next time?"
                        inputType="checkbox"
                        type="checkbox"
                        onChange={handleSaveDetailsCheckboxChange}
                        name="saveDetailsCheckbox"
                        checked={saveDetailsCheckboxState}
                      />
                    </div>
                  </div>
                )}
                <div className="email-checkbox">
                  <CheckboxInput
                    label="I'd like to speak to someone"
                    inputType="checkbox"
                    type="checkbox"
                    onChange={handleSupportCheckboxChange}
                    name="anonymous"
                    checked={supportCheckboxState}
                  />
                </div>
                {supportCheckboxState && (
                  <>
                    <div className="support-message">
                      We'll try and contact you this week
                    </div>
                    <div>
                      <FormInput
                        inputType="guestEmail"
                        type="email"
                        required
                        onChange={handleChange}
                        name="email"
                        placeholder="enter email"
                        value={email}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            <Button type="submit" buttonType="newPrayerSubmit">
              Submit
            </Button>
          </form>
        </div>
      </div>
    </>
  );
};

export default SubmitPrayerForm;
